import { useId, useMemo, useRef, useState } from "react";
import { money, saveByMonth } from "../lib/calc";

type Props = {
  monthlySave: number;
  switchingCost: number;
  breakEvenMonths: number | null;
  years: number;
};

export function BreakEvenChart({ monthlySave, switchingCost, breakEvenMonths, years }: Props) {
  const titleId = useId();
  const descId = useId();
  const svgRef = useRef<SVGSVGElement>(null);
  const horizon = Math.min(years * 12, Math.max(18, (breakEvenMonths ?? 12) * 3, 24));
  const points = useMemo(() => saveByMonth(monthlySave, horizon), [monthlySave, horizon]);
  const start = breakEvenMonths != null ? Math.min(breakEvenMonths, points.length - 1) : Math.min(12, points.length - 1);
  const [index, setIndex] = useState(start);
  const i = Math.min(index, points.length - 1);
  const point = points[i] ?? points[0];
  const ahead = point.saved >= switchingCost;

  const w = 320;
  const h = 200;
  const padL = 44;
  const padR = 12;
  const padT = 16;
  const padB = 28;
  const maxY = Math.max(switchingCost, points[points.length - 1]?.saved ?? 1, 1);
  const innerW = w - padL - padR;
  const innerH = h - padT - padB;
  const last = points[points.length - 1]?.month || 1;

  function x(month: number) {
    return padL + (month / last) * innerW;
  }
  function y(v: number) {
    return padT + innerH - (v / maxY) * innerH;
  }

  const d = points
    .map((p, n) => `${n === 0 ? "M" : "L"} ${x(p.month).toFixed(1)} ${y(p.saved).toFixed(1)}`)
    .join(" ");
  const area = `${d} L ${x(last).toFixed(1)} ${(padT + innerH).toFixed(1)} L ${x(0).toFixed(1)} ${(padT + innerH).toFixed(1)} Z`;

  function setFromClientX(clientX: number) {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const t01 = (clientX - rect.left - (padL / w) * rect.width) / ((innerW / w) * rect.width);
    setIndex(Math.round(Math.min(1, Math.max(0, t01)) * (points.length - 1)));
  }

  return (
    <figure className="plot plot-scrub">
      <div className="plot-readout" aria-live="polite">
        <span>Month {point.month}</span>
        <strong>{money(point.saved)}</strong>
        <em>
          {ahead
            ? `ahead of the ${money(switchingCost)} cost to move`
            : `still covering the ${money(switchingCost)} cost to move`}
        </em>
      </div>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${w} ${h}`}
        role="slider"
        tabIndex={0}
        aria-labelledby={titleId}
        aria-describedby={descId}
        aria-valuemin={0}
        aria-valuemax={points.length - 1}
        aria-valuenow={i}
        aria-valuetext={`Month ${point.month}, saved ${money(point.saved)}`}
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          setFromClientX(e.clientX);
        }}
        onPointerMove={(e) => {
          if (e.currentTarget.hasPointerCapture(e.pointerId)) setFromClientX(e.clientX);
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowRight" || e.key === "ArrowUp") {
            e.preventDefault();
            setIndex((n) => Math.min(points.length - 1, n + 1));
          }
          if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
            e.preventDefault();
            setIndex((n) => Math.max(0, n - 1));
          }
        }}
      >
        <title id={titleId}>Break-even after switching costs</title>
        <desc id={descId}>
          The filled area is cumulative monthly saving. The dashed line is the net cost to switch.
        </desc>
        <line x1={padL} y1={y(0)} x2={w - padR} y2={y(0)} className="plot-grid" />
        <line
          x1={padL}
          y1={y(switchingCost)}
          x2={w - padR}
          y2={y(switchingCost)}
          className="plot-dash"
        />
        <text x={padL - 6} y={y(switchingCost) + 4} className="plot-axis-label" textAnchor="end">
          cost
        </text>
        <path d={area} className="plot-fill mkt" />
        <path d={d} className="plot-line mkt" />
        {breakEvenMonths != null && breakEvenMonths <= horizon ? (
          <circle
            cx={x(breakEvenMonths)}
            cy={y(monthlySave * breakEvenMonths)}
            r={5}
            className="plot-mkt-dot"
          />
        ) : null}
        <line x1={x(point.month)} y1={padT} x2={x(point.month)} y2={padT + innerH} className="plot-play" />
        <circle cx={x(point.month)} cy={y(point.saved)} r={7} className="plot-mkt-dot" />
        <text x={padL} y={h - 8} className="plot-axis-label">
          Now
        </text>
        <text x={w - padR} y={h - 8} className="plot-axis-label" textAnchor="end">
          {horizon} mo
        </text>
      </svg>
      <figcaption>Filled area is money kept. Dashed line is the cost to switch. Drag a month.</figcaption>
    </figure>
  );
}
